import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../components/SEO';
import {
  EyeIcon,
  CodeBracketIcon,
  ArrowTopRightOnSquareIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

interface Project {
  id: string;
  title: string;
  description: string;
  category: string;
  technologies: string[];
  image: string;
  liveUrl?: string;
  demoUrl?: string;
  sourceUrl?: string;
  githubUrl?: string;
  client?: string;
  duration?: string;
  featured: boolean;
  published?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const Portfolio: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const categories = [
    { id: 'all', name: t('portfolio.filters.all') },
    { id: 'ai', name: t('portfolio.filters.ai') },
    { id: 'automation', name: t('portfolio.filters.automation') },
    { id: 'dataPipeline', name: t('portfolio.filters.dataPipeline') },
    { id: 'webDev', name: t('portfolio.filters.webDev') }
  ];

  // Load projects from API
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/portfolio');
        const result = await response.json();
        
        if (result.success && result.data) {
          setProjects(result.data);
        } else {
          // Fallback to hardcoded projects if API fails
          setProjects(fallbackProjects);
        }
      } catch (err) {
        console.error('Error loading projects:', err);
        // Use fallback data
        setProjects(fallbackProjects);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [t]);

  // Fallback projects (keep the existing hardcoded data as backup)
  const fallbackProjects: Project[] = [
    {
      id: '1',
      title: t('portfolio.project1.title'),
      description: t('portfolio.project1.description'),
      category: 'ai',
      technologies: ['Python', 'TensorFlow', 'FastAPI', 'React'],
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=AI%20predictive%20analytics%20dashboard%20with%20modern%20interface%20showing%20charts%20and%20data%20visualizations%2C%20dark%20theme%2C%20professional%20design&image_size=landscape_4_3',
      liveUrl: '#',
      githubUrl: '#',
      featured: true
    },
    {
      id: '2',
      title: t('portfolio.project2.title'),
      description: t('portfolio.project2.description'),
      category: 'automation',
      technologies: ['Node.js', 'Zapier', 'MongoDB', 'Express'],
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Business%20process%20automation%20workflow%20diagram%20with%20connected%20nodes%20and%20data%20flow%2C%20modern%20interface%2C%20blue%20and%20purple%20gradient&image_size=landscape_4_3',
      liveUrl: '#',
      featured: true
    },
    {
      id: '3',
      title: t('portfolio.project3.title'),
      description: t('portfolio.project3.description'),
      category: 'data',
      technologies: ['Python', 'Apache Airflow', 'PostgreSQL', 'Docker'],
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Data%20pipeline%20architecture%20visualization%20with%20flowing%20data%20streams%20and%20processing%20nodes%2C%20technical%20diagram%2C%20green%20accents&image_size=landscape_4_3',
      githubUrl: '#',
      featured: false
    },
    {
      id: '4',
      title: t('portfolio.project4.title'),
      description: t('portfolio.project4.description'),
      category: 'web',
      technologies: ['React', 'TypeScript', 'Tailwind CSS', 'Supabase'],
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Modern%20e-commerce%20website%20interface%20with%20clean%20design%2C%20product%20grid%2C%20shopping%20cart%2C%20responsive%20layout&image_size=landscape_4_3',
      liveUrl: '#',
      githubUrl: '#',
      featured: false
    },
    {
      id: '5',
      title: t('portfolio.project5.title'),
      description: t('portfolio.project5.description'),
      category: 'ai',
      technologies: ['Python', 'OpenAI API', 'Streamlit', 'LangChain'],
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=AI%20chatbot%20interface%20with%20conversation%20bubbles%20and%20modern%20chat%20UI%2C%20purple%20and%20blue%20theme&image_size=landscape_4_3',
      liveUrl: '#',
      featured: false
    },
    {
      id: '6',
      title: t('portfolio.project6.title'),
      description: t('portfolio.project6.description'),
      category: 'data',
      technologies: ['Power BI', 'SQL Server', 'Azure', 'Python'],
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Business%20intelligence%20dashboard%20with%20charts%2C%20KPIs%2C%20and%20data%20visualizations%2C%20corporate%20style%2C%20blue%20theme&image_size=landscape_4_3',
      liveUrl: '#',
      featured: false
    }
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  const featuredProjects = projects.filter(project => project.featured);

  const openProjectModal = (project: Project) => {
    setSelectedProject(project);
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
  };

  if (loading) {
    return (
      <>
        <SEO 
          title={t('portfolio.title')}
          description={t('portfolio.subtitle')}
          keywords="portfolio, AI projects, automation projects, Jean Bilbao"
        />
        <div className="space-y-12">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('portfolio.title')}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            {t('portfolio.subtitle')}
          </p>
        </motion.header>
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title={t('portfolio.title')}
        description={t('portfolio.subtitle')}
        keywords="portfolio, AI projects, automation projects, Jean Bilbao"
      />
      <div className="space-y-12">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
          {t('portfolio.title')}
        </h1>
        <p className="text-lg text-gray-300 max-w-3xl mx-auto">
          {t('portfolio.subtitle')}
        </p>
      </motion.header>

      {/* Featured Projects */}
      <motion.section
        ref={ref}
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="space-y-6"
      >
        <h2 className="text-2xl font-bold text-white mb-6">
          {t('portfolio.featured')}
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {featuredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              whileHover={{ scale: 1.02 }}
              className="group cursor-pointer"
              onClick={() => openProjectModal(project)}
            >
              <div className="bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden transition-all duration-300 hover:border-gray-600 hover:shadow-xl">
                <div className="relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {t('portfolio.featured')}
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-primary-300 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-300 mb-4 line-clamp-2">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.slice(0, 3).map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 3 && (
                      <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">
                        +{project.technologies.length - 3}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-3">
                      {(project.liveUrl || project.demoUrl) && (
                        <a 
                          href={project.liveUrl || project.demoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-400 hover:text-primary-300 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                        </a>
                      )}
                      {(project.githubUrl || project.sourceUrl) && (
                        <a 
                          href={project.githubUrl || project.sourceUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-white transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CodeBracketIcon className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                    <button 
                      className="text-primary-400 hover:text-primary-300 transition-colors"
                      onClick={() => openProjectModal(project)}
                      title={t('portfolio.viewDetails')}
                    >
                      <EyeIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Category Filter */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="space-y-6"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">
            {t('portfolio.allProjects')}
          </h2>
          <div className="flex items-center space-x-2 text-gray-400">
            <FunnelIcon className="w-5 h-5" />
            <span className="text-sm">{t('portfolio.filter')}</span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </motion.section>

      {/* Projects Grid */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="group cursor-pointer"
                onClick={() => openProjectModal(project)}
              >
                <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden transition-all duration-300 hover:border-gray-600">
                  <div className="relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-300 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 text-sm mb-3 line-clamp-2">
                      {project.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {project.technologies.slice(0, 2).map((tech) => (
                        <span
                          key={tech}
                          className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs"
                        >
                          {tech}
                        </span>
                      ))}
                      {project.technologies.length > 2 && (
                        <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">
                          +{project.technologies.length - 2}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-2">
                        {(project.liveUrl || project.demoUrl) && (
                          <a 
                            href={project.liveUrl || project.demoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary-400 hover:text-primary-300 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                          </a>
                        )}
                        {(project.githubUrl || project.sourceUrl) && (
                          <a 
                            href={project.githubUrl || project.sourceUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CodeBracketIcon className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      <button 
                        className="text-primary-400 hover:text-primary-300 transition-colors"
                        onClick={() => openProjectModal(project)}
                        title={t('portfolio.viewDetails')}
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </motion.section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeProjectModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-64 lg:h-80 object-cover"
                />
                <button
                  onClick={closeProjectModal}
                  className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-8">
                <h2 className="text-3xl font-bold text-white mb-4">
                  {selectedProject.title}
                </h2>
                
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {selectedProject.description}
                </p>
                
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {t('portfolio.technologies')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex space-x-4">
                  {(selectedProject.liveUrl || selectedProject.demoUrl) && (
                    <a 
                      href={selectedProject.liveUrl || selectedProject.demoUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-primary flex items-center space-x-2"
                    >
                      <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                      <span>{t('portfolio.liveDemo')}</span>
                    </a>
                  )}
                  {(selectedProject.githubUrl || selectedProject.sourceUrl) && (
                    <a 
                      href={selectedProject.githubUrl || selectedProject.sourceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-secondary flex items-center space-x-2"
                    >
                      <CodeBracketIcon className="w-5 h-5" />
                      <span>{t('portfolio.sourceCode')}</span>
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default Portfolio;